<template>
    <div class="product-row row col-xs-12">
        <div class="col-xs-12 col-md-5 pl-0 pr-0">
            <div class="col-xs-9 col-md-6 pl-2 text-left">
              <img v-if="product.image" :src="product.image.src.replace('.png', '_200x.png')"
                     :alt="product.image.alt"
                     class="img-responsive"/>
              <span v-else class="text-danger">No image</span>
            </div>
            <div class="col-xs-3 pl-0 pr-0 col-md-6">
                <div class="m-b-1">{{product.title}}</div>
                <el-select v-model="variantSelectedId"
                           :popper-class="'product-sizes'"
                           :disabled="!product.variant.id || disabledAP"
                           placeholder="Out Of Stock"
                           size="mini">
                    <el-option v-for="variant in product.variants"
                               :class="{'out-of-stock': variant.inventory_quantity < 1}"
                               :value="variant.id"
                               :key="variant.id"
                               :label="variant.title"/>
                </el-select>
                <div class="m-t-1">{{product.handle}}</div>
                <div class="m-t-1">
                    <PriceDisplay
                      :originalPrice="product.line_item ? Number(product.line_item.price) : Number(product.price)"
                      :updatePrice="Number(product.price)"
                    />
                </div>
            </div>
        </div>
        <div class="col-xs-11 col-md-6">
            <div class="col-xs-6 pl-0 pr-0 col-md-12">
                <span class="title col-xs-6 pl-0 pr-0 hidden-md hidden-lg">Stock</span>
                <span class="result col-md-4 col-xs-6">
                    <template v-if="inventory > 0">
                        <div class="text-success">{{inventory}}</div>
                    </template>
                    <template v-else>
                        <div class="text-warning">
                            This item appears to be <strong>out of stock</strong>.
                        </div>
                    </template>
                </span>
                <span class="title col-xs-6 pl-0 pr-0 hidden-md hidden-lg">QTY</span>
                <span class="col-md-2 col-xs-6">
                    <el-input v-model.number="quantity"
                              :disabled="disabled || disabledAP"
                              type="text"
                              class="result qty-result"/>
                    <div v-if="isInvalidQuantity" class="text-danger">
                        You have entered an <strong>invalid quantity</strong>.
                    </div>
                    <div v-else-if="quantity > inventory" class="text-danger">
                        This Quantity is <strong>greater than</strong> the quantity available.
                    </div>
                </span>
                <span class="title col-xs-6 pl-0 pr-0 hidden-md hidden-lg">Price</span>
                <span class="result price-result col-md-3 col-xs-6">
                    <el-input v-model="price"
                              v-mask="priceMask"
                              :disabled="disabled"
                              type="text"
                              class="price-result">
                              <span v-text="price"></span>
                    </el-input>
                </span>
                <span class="title col-xs-6 pl-0 pr-0 hidden-md hidden-lg">Total</span>
                <span class="result col-md-3 col-xs-6">{{product.total | currency}}</span>
            </div>
        </div>
        <div v-show="!disabledAP"
             @click="remove"
             class="col-xs-1 col-md-1 text-right">
            <i class="el-icon-delete text-danger result"></i>
        </div>
    </div>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex';
  import {currency} from '../../../filters';
  import {mask} from '../../../directives';
  import {
    ORDER_GET_DISABLED_STATUS_AP,
    ORDER_PRODUCTS_REMOVE,
    ORDER_PRODUCTS_UPDATE_VARIANT,
    ORDER_PRODUCTS_UPDATE_QUANTITY,
    ORDER_PRODUCTS_UPDATE_PRICE,
  } from '../../../store/types';
  import PriceDisplay from '../Shared/PriceDisplay';

  export default {
    name: 'OrderProduct',
    inject: ['$v'],
    props: {
      product: {
        type: Object,
        required: true,
        handle: {
          type: Object,
          required: true,
        },
        image: {
          type: Object,
          required: true,
          src: {
            type: String,
            required: true,
          },
        },
        quantity: {
          type: Number,
          required: true,
        },
        title: {
          type: String,
          required: true,
        },
        total: {
          type: Number,
          required: true,
        },
        variant: {
          type: Object,
          required: true,
        },
        variants: {
          type: Array,
          required: true,
        },
      },
    },
    components: {
          PriceDisplay,
      },
    filters: {
      currency,
    },
    directives: {
      mask,
    },
    computed: {
      ...mapGetters({
        disabledAP: ORDER_GET_DISABLED_STATUS_AP,
      }),
      isInvalidQuantity() {
        return this.$v.products.items.$each[this.$vnode.key].quantity.$invalid;
      },
      disabled: function() {
        return !this.product.variant.id;
      },
      inventory: function() {
          const {size} = this.product.variant;
          return size ? size.inventory : 0;
      },
      variantSelectedId: {
        set(variantId) {
          const productId = this.$vnode.key;
          const price = this.product.variant.new_price.toFixed(2);

          this.setVariantSelected({productId, variantId});
          // Over writing price with the last input price, rather than reloading
          // the price to the original price
          this.setProductPrice({productId, price});
          this.$v.products.$touch();
        },
        get() {
          return this.product.variant.id;
        },
      },
      quantity: {
        set(quantity) {
          const productId = this.$vnode.key;
          this.setProductQuantity({productId, quantity});
          this.$v.products.$touch();
        },
        get() {
          return this.product.quantity;
        },
      },
      price: {
        set(price) {
          const productId = this.$vnode.key;
          this.setProductPrice({productId, price});
          this.$v.products.$touch();
        },
        get() {
          return this.product.variant.new_price.toFixed(2);
        },
      },
    },
    methods: {
      ...mapActions({
        removeProduct: ORDER_PRODUCTS_REMOVE,
        setVariantSelected: ORDER_PRODUCTS_UPDATE_VARIANT,
        setProductQuantity: ORDER_PRODUCTS_UPDATE_QUANTITY,
        setProductPrice: ORDER_PRODUCTS_UPDATE_PRICE,
      }),
      remove() {
        const productId = this.$vnode.key;
        this.removeProduct(productId);
        this.$v.products.$touch();
      },
    },
    data() {
      return {
        priceMask: {
          alias: 'currency',
          prefix: '$',
          rightAlign: false,
          showMaskOnHover: false,
          autoUnmask: true,
          clearIncomplete: true,
        },
      };
    },
  };
</script>

<style lang="scss">
    .product-sizes {
        .el-select-dropdown__item.out-of-stock {
            &::after {
                content: " - Out of Stock";
            }

            > span {
                color: red;
                text-decoration: line-through;
            }
        }
    }
</style>
